import { type Template } from '@/types';
import type { DateRange } from '@heidi/design-system/date-range-picker';
import { type TemplateAuthorGroup, type TemplateCategory } from '@heidi/schema/ml-scribe';
import { type TemplateResponse } from '@heidi/templates/contracts';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

// const initialDateFrom = new Date(new Date().setHours(0, 0, 0, 0));

export type TemplateCategoryFilter = 'all' | 'favourites' | TemplateCategory;
export const templateCategoryFilterAtom = atom<TemplateCategoryFilter>('all');

/**
 * DS-1337: layout of the redesigned template library — the data `table` or a
 * `grid` of preview cards. Persisted to localStorage (not sessionStorage like
 * the filter atoms) so the chosen layout survives across sessions, mirroring
 * `cpdLibraryViewModeAtom`. Both layouts share the same filter atoms below, so
 * switching layout keeps the active search / type / date filters.
 */
export type TemplateLibraryLayout = 'table' | 'grid';
export const templateLibraryLayoutAtom = atomWithStorage<TemplateLibraryLayout>(
  'templateLibrary:layout',
  'table'
);

export const getDateAdjustedForTimezone = (dateInput: Date | string): Date => {
  if (typeof dateInput === 'string') {
    // Split the date string to get year, month, and day parts
    const parts = dateInput.split('-').map((part) => parseInt(part, 10));
    // Create a new Date object using the local timezone
    // Note: Month is 0-indexed, so subtract 1 from the month part
    const date = new Date(parts?.[0] || 0, parts?.[1] || 0 - 1, parts[2]);
    return date;
  } else {
    // If dateInput is already a Date object, return it directly
    return dateInput;
  }
};

// Atom for range
export const dateRangeAtom = atom<DateRange>({
  from: undefined,
  to: undefined,
});

export const datePreset = atom<string | undefined>(undefined);

export const deleteTemplateConfirmationAtom = atom<{
  isOpen: boolean;
  template: Template | undefined;
}>({
  isOpen: false,
  template: undefined,
});

// Helper to create typed sessionStorage
const createSessionStorage = <T,>() =>
  typeof globalThis.window === 'undefined'
    ? undefined
    : createJSONStorage<T>(() => globalThis.sessionStorage);

// Template table state atoms with sessionStorage persistence
export const templateTablePageAtom = atomWithStorage<number>(
  'templateTable:page',
  1,
  createSessionStorage<number>()
);

export const templateTableSearchAtom = atomWithStorage<string>(
  'templateTable:search',
  '',
  createSessionStorage<string>()
);

export const templateTableCreatedByFilterAtom = atomWithStorage<TemplateAuthorGroup[]>(
  'templateTable:createdByMulti',
  [],
  createSessionStorage<TemplateAuthorGroup[]>()
);

export const templateTableTypeFilterAtom = atomWithStorage<TemplateCategory[]>(
  'templateTable:typeMulti',
  [],
  createSessionStorage<TemplateCategory[]>()
);

export type TemplateSortField = keyof Template;
export type TemplateSortOrder = 'asc' | 'desc';

export interface TemplateTableSort {
  field: TemplateSortField;
  order: TemplateSortOrder;
}

export const templateTableSortAtom = atomWithStorage<TemplateTableSort>(
  'templateTable:sort',
  { field: 'updated_time', order: 'desc' },
  createSessionStorage<TemplateTableSort>()
);

/**
 * Sort state for the redesigned DataTable (DS-958). Unlike the legacy
 * {@link templateTableSortAtom}, `null` is a first-class "unsorted" value so the
 * column header can cycle ascending → descending → none on repeated clicks.
 * When `null`, rows fall back to the query's natural order.
 */
export const templateLibrarySortAtom = atomWithStorage<TemplateTableSort | null>(
  'templateLibrary:sort',
  { field: 'updated_time', order: 'desc' },
  createSessionStorage<TemplateTableSort | null>()
);

// Date range stored as ISO strings for serialization
export interface TemplateTableDateRange {
  from?: string;
  to?: string;
}

export const templateTableDateRangeAtom = atomWithStorage<TemplateTableDateRange>(
  'templateTable:dateRange',
  { from: undefined, to: undefined },
  createSessionStorage<TemplateTableDateRange>()
);

export const regulatoryGuidelineWarningModalTemplateAtom = atom<TemplateResponse>();
